import React, {useMemo, useRef, useState} from "react";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import {ButtonBase, ListItemText, Menu} from "@mui/material";
import {Box, Checkbox, Colors, Text} from "@bounce/shared/ui";
import {MenuItem, Tooltip} from "../../../ui/components";
import {ReachSegment} from "../../../apis/requests/templates/types";
import {marketingSegmentUrl} from "../../segments/segmentLinks";
import {groupSegmentTags, selectableSegments} from "../../templates/segmentTagSelection";

const ADD_SEGMENT_LABEL = "Add segment…";
const EXCLUSIVE_LABEL = "Exclusive";
const OPEN_SEGMENT_TOOLTIP = "Open in new tab";
const REMOVE_SEGMENT_TOOLTIP = "Remove";
const LINK_HELP = "Click a segment to open it in a new tab. Your promo draft stays open here.";
const SELECTION_CONTROL_SX = {margin: "2px 12px 2px 6px", padding: 0};
const CHIP_HEIGHT = 28;
const CHIP_ICON_SIZE = 12;
const FIELD_MIN_HEIGHT = 40;
const ADD_TRIGGER_MIN_WIDTH = 120;
const FOCUS_RING = {outline: `2px solid ${Colors.blue._700}`, outlineOffset: 2};

type Props = {
  segments: ReachSegment[];
  value: number[];
  onChange: (value: number[]) => void;
};

const segmentLabel = (segment: ReachSegment) =>
  segment.type === "EXCLUSIVE" ? `${EXCLUSIVE_LABEL} · ${segment.name}` : segment.name;

const SegmentChip = ({segment, onRemove}: {segment: ReachSegment; onRemove: () => void}) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      height: CHIP_HEIGHT,
      borderRadius: "6px",
      backgroundColor: Colors.blue._100,
      color: Colors.blue._800,
    }}
  >
    <Tooltip title={OPEN_SEGMENT_TOOLTIP}>
      <Box
        component="a"
        href={marketingSegmentUrl(segment.id)}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          height: CHIP_HEIGHT,
          pl: 1.25,
          pr: 0.75,
          borderRadius: "6px 0 0 6px",
          fontSize: 13,
          fontWeight: 500,
          color: "inherit",
          textDecoration: "none",
          "&:hover": {backgroundColor: Colors.blue._200, textDecoration: "underline"},
          "&:focus-visible": FOCUS_RING,
        }}
      >
        {segmentLabel(segment)}
        <NorthEastIcon sx={{fontSize: CHIP_ICON_SIZE}} />
      </Box>
    </Tooltip>
    <Tooltip title={REMOVE_SEGMENT_TOOLTIP}>
      <ButtonBase
        aria-label={`Remove ${segment.name}`}
        onClick={onRemove}
        sx={{
          width: 26,
          height: CHIP_HEIGHT,
          borderLeft: `1px solid ${Colors.blue._200}`,
          borderRadius: "0 6px 6px 0",
          color: "inherit",
          "&:hover": {backgroundColor: Colors.blue._200, color: Colors.blue._900},
          "&.Mui-focusVisible": FOCUS_RING,
        }}
      >
        <CloseIcon sx={{fontSize: CHIP_ICON_SIZE}} />
      </ButtonBase>
    </Tooltip>
  </Box>
);

const PromoSegmentsSelect = ({segments, value, onChange}: Props) => {
  const fieldRef = useRef<HTMLDivElement>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLDivElement | null>(null);
  const options = useMemo(() => selectableSegments(groupSegmentTags(segments, false)), [segments]);
  const segmentById = useMemo(() => new Map(options.map((segment) => [segment.id, segment])), [options]);
  const selectedSegments = value.flatMap((id) => segmentById.get(id) ?? []);

  const toggle = (segmentId: number) =>
    onChange(value.includes(segmentId) ? value.filter((id) => id !== segmentId) : [...value, segmentId]);

  return (
    <>
      <Box
        ref={fieldRef}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 0.75,
          minHeight: FIELD_MIN_HEIGHT,
          px: 1,
          py: 0.625,
          boxSizing: "border-box",
          backgroundColor: Colors.bw.white,
          border: `1px solid ${menuAnchor ? Colors.blue._500 : Colors.tertiary._300}`,
          borderRadius: "8px",
        }}
      >
        {selectedSegments.map((segment) => (
          <SegmentChip key={segment.id} segment={segment} onRemove={() => toggle(segment.id)} />
        ))}
        <ButtonBase
          onClick={() => setMenuAnchor(fieldRef.current)}
          sx={{
            flex: 1,
            minWidth: ADD_TRIGGER_MIN_WIDTH,
            height: CHIP_HEIGHT,
            justifyContent: "space-between",
            fontSize: 13,
            color: Colors.grey._500,
            borderRadius: "6px",
            "&.Mui-focusVisible": FOCUS_RING,
          }}
        >
          {ADD_SEGMENT_LABEL}
          <KeyboardArrowDownIcon fontSize="small" />
        </ButtonBase>
      </Box>
      <Text size={12} sx={{color: Colors.grey._600, mt: 0.75}}>
        {LINK_HELP}
      </Text>
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        slotProps={{paper: {sx: {width: menuAnchor?.clientWidth}}}}
      >
        {options.map((segment) => (
          <MenuItem key={segment.id} onClick={() => toggle(segment.id)}>
            <Checkbox checked={value.includes(segment.id)} sx={SELECTION_CONTROL_SX} />
            <ListItemText
              primary={segment.name}
              secondary={segment.type === "EXCLUSIVE" ? EXCLUSIVE_LABEL : undefined}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default PromoSegmentsSelect;
